import { collabStateManager } from '@databases/collab-state-manager';
import { type Database } from '@databases/model/database';
import { FieldType } from '@databases/model/field';

export type AiSummaryResultRow = {
  entryId: string;
  testId: string;
  generatedSummary: string;
};

export type AiSummaryResults = {
  targetFieldId: string;
  targetFieldName: string;
  results: AiSummaryResultRow[];
};

function requireSingleFieldId(
  database: Database,
  predicate: (fieldId: string) => boolean,
  description: string
): string {
  const fieldIds = database.fields.filter(predicate);
  if (fieldIds.length !== 1) {
    throw new Error(`Expected exactly one ${description}; found ${fieldIds.length}`);
  }
  return fieldIds[0];
}

/**
 * Read generated AI Summary values for browser-based evaluation only.
 *
 * The regular readContent API intentionally excludes AI fields so generated
 * values cannot become source context. Keep that contract unchanged and use
 * this narrow projection only when validating an AI Summary eval duplicate.
 * This API is intentionally available in deployed builds because browser-based
 * evals run against deployed Databases instances.
 */
export function extractAiSummaryResults(
  database: Database,
  testIdFieldName = 'Test ID'
): AiSummaryResults {
  const targetFieldId = requireSingleFieldId(
    database,
    (fieldId) => database.fieldsById[fieldId]?.type === FieldType.AiSummary,
    'AI Summary field'
  );
  const targetField = database.fieldsById[targetFieldId];

  const testIdFieldId = requireSingleFieldId(
    database,
    (fieldId) => database.fieldsById[fieldId]?.name === testIdFieldName,
    `${JSON.stringify(testIdFieldName)} field`
  );
  const testIdField = database.fieldsById[testIdFieldId];
  if (testIdField.type !== FieldType.Text) {
    throw new Error(
      `Expected ${JSON.stringify(testIdFieldName)} field to be text; found ${testIdField.type}`
    );
  }

  const results = database.entries.map((entryId: string) => {
    const entry = database.entriesById[entryId];
    const testIdValue = entry?.getValue(testIdFieldId)?.toJSON();
    if (
      testIdValue?.type !== FieldType.Text ||
      typeof testIdValue.value.plain !== 'string'
    ) {
      throw new Error(`Entry ${entryId} has no text ${testIdFieldName} value`);
    }

    const summaryValue = entry.getValue(targetFieldId)?.toJSON();
    if (
      summaryValue?.type !== FieldType.AiSummary ||
      typeof summaryValue.value.plain !== 'string'
    ) {
      throw new Error(
        `Entry ${entryId} has no generated value for AI Summary field ${JSON.stringify(targetField.name)}`
      );
    }

    return {
      entryId,
      testId: testIdValue.value.plain,
      generatedSummary: summaryValue.value.plain
    };
  });

  return {
    targetFieldId,
    targetFieldName: targetField.name,
    results
  };
}

export function readAiSummaryResults(contentId: string): Promise<AiSummaryResults> {
  return Promise.resolve().then(() => {
    const database = collabStateManager.getCollabManager(contentId)?.database;
    if (!database) {
      throw new Error('Database not found');
    }
    return extractAiSummaryResults(database);
  });
}
